import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { getNotificationTypeInfo, formatNotificationTime, detectNotificationType, detectNotificationPriority } from '../utils/notificationUtils';
import { useAuth } from '../context/AuthContext';
import usePwaDisplayMode from '../hooks/usePwaDisplayMode';
import '../css/NotificationBell.css';

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPwaExperience } = usePwaDisplayMode();
  const hasActiveSession = (() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return false;
      const parsedUser = JSON.parse(storedUser);
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      return Boolean(authToken && parsedUser && (parsedUser.id || parsedUser._id || parsedUser.username));
    } catch (error) {
      return false;
    }
  })();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState('ALL');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const dropdownRef = useRef(null);
  const notificationListRef = useRef(null);
  const filterTabsRef = useRef(null);
  
  // Ref để lưu trữ ID thông báo đã xử lý
  const processedNotifications = useRef(new Set());
  
  // Ref để lưu trữ aggressive polling interval
  const aggressivePollingRef = useRef(null);

  // Hàm tạo danh sách danh mục thông báo dựa trên role
  const getNotificationCategories = useCallback((userRole) => {
    const baseCategories = [
      { value: 'ALL', label: 'Tất cả', icon: 'fas fa-list' }
    ];

    // Danh mục chung cho tất cả user
    const commonCategories = [
      { value: 'SYSTEM', label: 'Hệ thống', icon: 'fas fa-cog' },
      { value: 'EVALUATION', label: 'Đánh giá', icon: 'fas fa-clipboard-check' },
      { value: 'OTHER', label: 'Khác', icon: 'fas fa-ellipsis-h' }
    ];

    // Danh mục theo role
    const roleBasedCategories = [];

    switch (userRole) {
      case 'SUPER_ADMIN':
      case 'ADMIN':
        roleBasedCategories.push(
          { value: 'HR', label: 'Nhân sự', icon: 'fas fa-user-tie' },
          { value: 'REPORT', label: 'Báo cáo', icon: 'fas fa-file-alt' },
          { value: 'REQUEST', label: 'Yêu cầu', icon: 'fas fa-hand-paper' },
          { value: 'LEAVE_REQUEST', label: 'Nghỉ phép', icon: 'fas fa-calendar-times' },
          { value: 'APPROVAL', label: 'Phê duyệt', icon: 'fas fa-check-circle' },
          { value: 'VEHICLE', label: 'Xe cộ', icon: 'fas fa-car' },
          { value: 'INCIDENT', label: 'Sự cố', icon: 'fas fa-exclamation-triangle' },
          { value: 'TASK', label: 'Nhiệm vụ', icon: 'fas fa-tasks' }
        );
        break;

      case 'LEVEL_I_MANAGER':
      case 'LEVEL_II_MANAGER':
        roleBasedCategories.push(
          { value: 'HR', label: 'Nhân sự', icon: 'fas fa-user-tie' },
          { value: 'REPORT', label: 'Báo cáo', icon: 'fas fa-file-alt' },
          { value: 'REQUEST', label: 'Yêu cầu', icon: 'fas fa-hand-paper' },
          { value: 'LEAVE_REQUEST', label: 'Nghỉ phép', icon: 'fas fa-calendar-times' },
          { value: 'APPROVAL', label: 'Phê duyệt', icon: 'fas fa-check-circle' },
          { value: 'VEHICLE', label: 'Xe cộ', icon: 'fas fa-car' },
          { value: 'TASK', label: 'Nhiệm vụ', icon: 'fas fa-tasks' }
        );
        break;

      case 'DRIVER':
        roleBasedCategories.push(
          { value: 'VEHICLE', label: 'Xe cộ', icon: 'fas fa-car' },
          { value: 'TASK', label: 'Nhiệm vụ', icon: 'fas fa-tasks' },
          { value: 'APPROVAL', label: 'Phê duyệt', icon: 'fas fa-check-circle' }
        );
        break;

      case 'EMPLOYEE':
        roleBasedCategories.push(
          { value: 'REPORT', label: 'Báo cáo', icon: 'fas fa-file-alt' },
          { value: 'LEAVE_REQUEST', label: 'Nghỉ phép', icon: 'fas fa-calendar-times' },
          { value: 'APPROVAL', label: 'Phê duyệt', icon: 'fas fa-check-circle' },
          { value: 'TASK', label: 'Nhiệm vụ', icon: 'fas fa-tasks' }
        );
        break;

      case 'ACCOUNTANT':
        roleBasedCategories.push(
          { value: 'REPORT', label: 'Báo cáo', icon: 'fas fa-file-alt' },
          { value: 'REQUEST', label: 'Yêu cầu', icon: 'fas fa-hand-paper' },
          { value: 'APPROVAL', label: 'Phê duyệt', icon: 'fas fa-check-circle' },
          { value: 'VEHICLE', label: 'Xe cộ', icon: 'fas fa-car' }
        );
        break;

      default:
        // Cho các role khác hoặc chưa đăng nhập
        roleBasedCategories.push(
          { value: 'REPORT', label: 'Báo cáo', icon: 'fas fa-file-alt' },
          { value: 'TASK', label: 'Nhiệm vụ', icon: 'fas fa-tasks' }
        );
        break;
    }

    return [...baseCategories, ...roleBasedCategories, ...commonCategories];
  }, []);

  // Danh sách các danh mục thông báo dựa trên role
  const notificationCategories = getNotificationCategories(user?.role);

  // Hàm lọc thông báo theo danh mục
  const getFilteredNotifications = useCallback((notifications, filter) => {
    if (filter === 'ALL') return notifications;
    
    return notifications.filter(notification => {
      const detectedType = detectNotificationType(notification.message, notification.type);
      
      switch (filter) {
        case 'HR':
          return ['HR_CONTRACT_EXPIRED', 'HR_CONTRACT_EXPIRING', 'HR_MISSING_DATA', 'HR_BIRTHDAY', 'HR_MISSING_DOCUMENT', 'HR_GENERAL'].includes(detectedType) ||
                 notification.type?.startsWith('HR_') ||
                 (notification.message.toLowerCase().includes('nhân sự') && !notification.message.toLowerCase().includes('xe'));
        case 'REPORT':
          return ['REPORT', 'WORK_REPORT', 'DAILY_REPORT', 'WEEKLY_REPORT', 'MONTHLY_REPORT', 'PROJECT_REPORT'].includes(detectedType) || 
                 notification.message.toLowerCase().includes('báo cáo') ||
                 notification.message.toLowerCase().includes('báo công') ||
                 notification.message.toLowerCase().includes('report');
        case 'VEHICLE':
          return ['VEHICLE_EXPIRED', 'VEHICLE_EXPIRING', 'VEHICLE_MAINTENANCE', 'VEHICLE_FUEL', 'VEHICLE_INSURANCE'].includes(detectedType);
        case 'REQUEST':
          return detectedType === 'REQUEST';
        case 'LEAVE_REQUEST':
          return detectedType === 'LEAVE_REQUEST';
        case 'APPROVAL':
          return detectedType === 'APPROVAL';
        case 'SYSTEM':
          return detectedType === 'SYSTEM';
        case 'INCIDENT':
          return detectedType === 'INCIDENT';
        case 'TASK':
          return detectedType === 'TASK';
        case 'EVALUATION':
          return detectedType === 'EVALUATION' || 
                 notification.message.toLowerCase().includes('đánh giá') ||
                 notification.message.toLowerCase().includes('evaluation');
        case 'OTHER':
          return !['REPORT', 'WORK_REPORT', 'DAILY_REPORT', 'WEEKLY_REPORT', 'MONTHLY_REPORT', 'PROJECT_REPORT', 'REQUEST', 'LEAVE_REQUEST', 'APPROVAL', 'SYSTEM', 'INCIDENT', 'TASK', 'EVALUATION', 'VEHICLE_EXPIRED', 'VEHICLE_EXPIRING', 'VEHICLE_MAINTENANCE', 'VEHICLE_FUEL', 'VEHICLE_INSURANCE', 'HR_CONTRACT_EXPIRED', 'HR_CONTRACT_EXPIRING', 'HR_MISSING_DATA', 'HR_BIRTHDAY', 'HR_MISSING_DOCUMENT', 'HR_GENERAL'].includes(detectedType) &&
                 !notification.type?.startsWith('HR_') &&
                 !notification.message.toLowerCase().includes('báo cáo') &&
                 !notification.message.toLowerCase().includes('báo công') &&
                 !notification.message.toLowerCase().includes('report') &&
                 !notification.message.toLowerCase().includes('đánh giá');
        default:
          return true;
      }
    });
  }, []);

  // Hàm xử lý thay đổi filter
  const handleFilterChange = useCallback((newFilter) => {
    setSelectedFilter(newFilter);
    // Reset pagination khi thay đổi filter
    setCurrentPage(1);
    setHasMore(true);
  }, []);

  // Hàm kiểm tra khả năng scroll
  const checkScrollability = useCallback(() => {
    if (filterTabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterTabsRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Hàm scroll trái
  const scrollToLeft = useCallback(() => {
    if (filterTabsRef.current) {
      filterTabsRef.current.scrollBy({
        left: -150,
        behavior: 'smooth'
      });
    }
  }, []);

  // Hàm scroll phải
  const scrollToRight = useCallback(() => {
    if (filterTabsRef.current) {
      filterTabsRef.current.scrollBy({
        left: 150,
        behavior: 'smooth'
      });
    }
  }, []);

  // Effect để kiểm tra scroll khi component mount và khi filter thay đổi
  useEffect(() => {
    const checkScroll = () => {
      setTimeout(checkScrollability, 100);
    };
    
    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScrollability, showDropdown]);

  // Effect để lắng nghe scroll event trên filter tabs
  useEffect(() => {
    const filterTabs = filterTabsRef.current;
    if (filterTabs) {
      filterTabs.addEventListener('scroll', checkScrollability);
      return () => {
        filterTabs.removeEventListener('scroll', checkScrollability);
      };
    }
  }, [checkScrollability, showDropdown]);

  // useCallback for stable function references
  const fetchUnreadCount = useCallback(async () => {
    if (!hasActiveSession) return;

    try {
      console.log('[NotificationBell] Fetching unread count from server');
      const response = await API.get('/notifications/unread-count', {
        retry: 0,
        skipErrorInterceptorLog: true
      });
      console.log('[NotificationBell] Unread count response:', response.data);
      
      // Đảm bảo response.data tồn tại và có unreadCount
      if (response.data && typeof response.data.unreadCount === 'number') {
        console.log('[NotificationBell] Setting unread count to:', response.data.unreadCount);
        setUnreadCount(response.data.unreadCount);
      } else {
        console.warn('[NotificationBell] Invalid unread count response:', response.data);
        // Không reset về 0 nếu response không hợp lệ
      }
    } catch (error) {
      const isTransient = (
        error?.code === 'ERR_NETWORK'
        || error?.code === 'ECONNABORTED'
        || [502, 503, 504].includes(error?.response?.status)
      );
      if (!isTransient) {
        console.error('[NotificationBell] Error fetching notification count:', error);
      }
      // Không reset về 0 khi có lỗi, giữ nguyên giá trị cũ
    }
  }, [hasActiveSession]);

  const fetchRecentNotifications = useCallback(async (page = 1, append = false) => {
    if (!hasActiveSession) return;

    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const limit = 20; // Tăng số lượng thông báo mỗi lần tải để hiệu quả hơn
      const response = await API.get(`/notifications?page=${page}&limit=${limit}&sort=createdAt_desc`); 
      console.log('Notifications response:', response.data);

      if (response.data && response.data.notifications) {
        const newNotifications = response.data.notifications;
        const pagination = response.data.pagination || {};
        
        // Append thông báo mới vào danh sách hiện tại
        setNotifications(prev => {
          if (append) {
            // Lọc trùng lặp dựa trên _id
            const existingIds = new Set(prev.map(n => n._id));
            const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n._id));
            return [...prev, ...uniqueNewNotifications];
          } else {
            return newNotifications;
          }
        });
        
        setCurrentPage(pagination.currentPage || page);
        setTotalPages(pagination.totalPages || 1);
        
        // Kiểm tra xem còn thông báo để tải không
        const hasNextPage = pagination.hasNextPage !== undefined ? pagination.hasNextPage : newNotifications.length === limit;
        setHasMore(hasNextPage);
        
        console.log(`[NotificationBell] Loaded page ${page}, hasMore: ${hasNextPage}, total notifications: ${append ? 'prev + ' + newNotifications.length : newNotifications.length}`);
        
      } else if (Array.isArray(response.data)) { // Fallback for different API structure
        const newNotifications = response.data;
        setNotifications(prev => {
          if (append) {
            const existingIds = new Set(prev.map(n => n._id));
            const uniqueNewNotifications = newNotifications.filter(n => !existingIds.has(n._id));
            return [...prev, ...uniqueNewNotifications];
          } else {
            return newNotifications;
          }
        });
        
        // Nếu số lượng trả về ít hơn limit, có nghĩa là đã hết
        setHasMore(newNotifications.length === limit);
        
      } else {
        if (!append) {
          setNotifications([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      if (!append) {
        setNotifications([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [hasActiveSession]);

  // Hàm tải thêm thông báo cho infinite scroll
  const loadMoreNotifications = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    
    const nextPage = currentPage + 1;
    await fetchRecentNotifications(nextPage, true);
  }, [hasMore, loadingMore, currentPage, fetchRecentNotifications]);

  // Hàm xử lý scroll event với debouncing
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Tải thêm khi scroll đến 80% chiều cao (thay vì chờ đến cuối)
    const shouldLoadMore = scrollPercentage >= 0.8;
    
    console.log(`[NotificationBell] Scroll: ${Math.round(scrollPercentage * 100)}%, hasMore: ${hasMore}, loadingMore: ${loadingMore}`);
    
    if (shouldLoadMore && hasMore && !loadingMore) {
      console.log('[NotificationBell] Triggering load more notifications');
      loadMoreNotifications();
    }
  }, [hasMore, loadingMore, loadMoreNotifications]);

  // Reset pagination khi mở dropdown
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setHasMore(true);
    setNotifications([]);
  }, []);

  const handleNewNotification = useCallback((event) => {
    console.log('[NotificationBell] Processing notification event:', event.detail);
    
    // Kiểm tra cấu trúc event.detail
    if (!event.detail) {
      console.warn('[NotificationBell] Invalid event structure - missing detail:', event);
      return;
    }
    
    const notification = event.detail.notification;
    const unreadCountFromEvent = event.detail.unreadCount;
    
    // Kiểm tra notification
    if (!notification) {
      console.warn('[NotificationBell] Missing notification in event detail:', event.detail);
      // Vẫn cập nhật số lượng thông báo chưa đọc nếu có
      if (typeof unreadCountFromEvent === 'number') {
        console.log('[NotificationBell] Setting unread count from event:', unreadCountFromEvent);
        setUnreadCount(unreadCountFromEvent);
      } else {
        console.log('[NotificationBell] Fetching unread count due to missing notification');
        fetchUnreadCount();
      }
      return;
    }
    
    // Kiểm tra ID thông báo
    if (!notification._id) {
      console.warn('[NotificationBell] Notification missing _id:', notification);
      fetchUnreadCount();
      return;
    }

    const notificationKey = notification._id;
    // Kiểm tra xem thông báo đã được xử lý chưa
    if (processedNotifications.current.has(notificationKey)) {
      console.log('[NotificationBell] Notification already processed, skipping:', notificationKey);
      return;
    }
    
    // Đánh dấu thông báo đã được xử lý
    processedNotifications.current.add(notificationKey);
    console.log('[NotificationBell] Added to processed notifications:', notificationKey);

    // Cập nhật số lượng thông báo chưa đọc
    if (typeof unreadCountFromEvent === 'number') {
      console.log('[NotificationBell] Setting unread count from event:', unreadCountFromEvent);
      setUnreadCount(unreadCountFromEvent);
    } else {
      console.log('[NotificationBell] Fetching unread count from server');
      fetchUnreadCount();
    }

    // Thêm thông báo vào danh sách, đảm bảo không trùng lặp
    setNotifications((prevNotifications) => {
      const filteredPrev = prevNotifications.filter(n => n._id !== notification._id);
      const newList = [notification, ...filteredPrev];
      return newList; // Không giới hạn kích thước cho infinite scroll
    });
    
    // Nếu dropdown đang mở, làm mới danh sách
    if (showDropdown) {
      console.log('[NotificationBell] Dropdown is open, refreshing notifications list');
      // Reset và tải lại từ đầu để đảm bảo thông báo mới xuất hiện ở đầu
      resetPagination();
      fetchRecentNotifications(1, false);
    }

  }, [fetchUnreadCount, setNotifications, showDropdown, fetchRecentNotifications, resetPagination]);

  // Auto-load more notifications nếu dropdown mở và danh sách ít thông báo
  useEffect(() => {
    if (showDropdown && notifications.length < 20 && hasMore && !loading && !loadingMore) {
      console.log('[NotificationBell] Auto-loading more notifications to fill dropdown');
      loadMoreNotifications();
    }
  }, [showDropdown, notifications.length, hasMore, loading, loadingMore, loadMoreNotifications]);

  useEffect(() => {
    if (!hasActiveSession) {
      setUnreadCount(0);
      setNotifications([]);
      return;
    }

    console.log('[NotificationBell] Component mounted - Fetching initial unread count');
    fetchUnreadCount(); // Fetch initial unread count

    // Thiết lập polling để cập nhật số lượng thông báo chưa đọc mỗi 10 giây
    // Giảm thời gian polling để đảm bảo cập nhật thường xuyên hơn
    const pollingInterval = setInterval(() => {
      console.log('[NotificationBell] Polling for unread count');
      fetchUnreadCount();
    }, 10000); // 10 giây - giảm thời gian để cập nhật nhanh hơn

    // Thêm một lần fetch sau 2 giây để đảm bảo dữ liệu được cập nhật
    const initialDelayedFetch = setTimeout(() => {
      console.log('[NotificationBell] Delayed initial fetch for unread count');
      fetchUnreadCount();
    }, 2000);

    // Thêm một lần fetch sau 5 giây để đảm bảo dữ liệu được cập nhật
    const secondDelayedFetch = setTimeout(() => {
      console.log('[NotificationBell] Second delayed fetch for unread count');
      fetchUnreadCount();
    }, 5000);

    // Xử lý sự kiện notification_received
    const notificationHandler = (event) => {
      console.log('[NotificationBell] Received notification event:', event.detail);
      handleNewNotification(event);
    };
    window.addEventListener('notification_received', notificationHandler);

    // Xử lý sự kiện WebSocket kết nối thành công
    const wsConnectedHandler = () => {
      console.log('[NotificationBell] WebSocket connected, refreshing unread count');
      fetchUnreadCount();
    };
    window.addEventListener('ws_connected', wsConnectedHandler);

    // Xử lý sự kiện WebSocket đóng kết nối
    const wsDisconnectedHandler = () => {
      console.log('[NotificationBell] WebSocket disconnected, switching to aggressive polling');
      // Khi WebSocket đóng, tăng tần suất polling
      const aggressivePolling = setInterval(() => {
        console.log('[NotificationBell] Aggressive polling for unread count');
        fetchUnreadCount();
      }, 5000); // 5 giây khi không có WebSocket
      
      // Lưu interval ID để có thể clear khi component unmount
      // Sử dụng ref để tránh lỗi closure
      aggressivePollingRef.current = aggressivePolling;
    };
    window.addEventListener('ws_disconnected', wsDisconnectedHandler);

    // Thêm sự kiện focus để cập nhật khi tab được focus lại
    const handleFocus = () => {
      console.log('[NotificationBell] Window focused, refreshing unread count');
      fetchUnreadCount();
    };
    window.addEventListener('focus', handleFocus);

    // Thêm sự kiện click để cập nhật khi người dùng click vào bất kỳ đâu trên trang
    // Điều này giúp đảm bảo số lượng thông báo được cập nhật khi người dùng tương tác với trang
    const handleClick = () => {
      console.log('[NotificationBell] User clicked, refreshing unread count');
      fetchUnreadCount();
    };
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('notification_received', notificationHandler);
      window.removeEventListener('ws_connected', wsConnectedHandler);
      window.removeEventListener('ws_disconnected', wsDisconnectedHandler);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('click', handleClick);
      
      clearInterval(pollingInterval);
      clearTimeout(initialDelayedFetch);
      clearTimeout(secondDelayedFetch);
      
      // Clear aggressive polling nếu có
      if (aggressivePollingRef.current) {
        clearInterval(aggressivePollingRef.current);
      }
      
      // processedNotifications.current.clear(); // Clearing might be too aggressive if component re-mounts
    };
  }, [fetchUnreadCount, handleNewNotification, hasActiveSession]); // Correct dependencies

  // Xử lý click outside để đóng dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleBellClick = () => {
    if (!hasActiveSession) {
      navigate('/login');
      return;
    }

    console.log('[NotificationBell] handleBellClick triggered. Current showDropdown:', showDropdown);
    
    // Nếu là PWA mode, chuyển đến trang AllNotifications
    if (isPwaExperience) {
      console.log('[NotificationBell] PWA mode detected, navigating to /all-notifications');
      navigate('/all-notifications');
      return;
    }
    
    // Desktop mode: hiển thị dropdown như bình thường
    // Luôn fetch unread count khi click vào bell, bất kể đang mở hay đóng dropdown
    fetchUnreadCount();
    
    if (!showDropdown) {
      // Reset pagination và tải lại từ đầu khi mở dropdown
      resetPagination();
      fetchRecentNotifications(1, false); // Refresh when opening
    }
    
    // Thêm một lần fetch sau 500ms để đảm bảo dữ liệu được cập nhật sau khi dropdown mở
    setTimeout(() => {
      console.log('[NotificationBell] Delayed fetch after bell click');
      fetchUnreadCount();
    }, 500);
    
    setShowDropdown(!showDropdown);
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await API.post(`/notifications/${notificationId}/mark-read`);
      if (response.data && response.data.notification) {
        const updatedNotification = response.data.notification;
        setNotifications((prev) =>
          prev.map((n) => (n._id === updatedNotification._id ? updatedNotification : n))
        );
        // Use count from response if available, otherwise fetch
        if (typeof response.data.unreadCount === 'number') {
          setUnreadCount(response.data.unreadCount);
        } else {
          fetchUnreadCount();
        }
      } else {
        // Fallback: optimistically update and fetch count
        setNotifications((prev) =>
          prev.map((n) => (n._id === notificationId ? { ...n, isRead: true } : n))
        );
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Add user feedback, e.g., toast error
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await API.post('/notifications/mark-all-read');
      // Optimistically update all visible notifications to read
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      // Use count from response if available (should be 0), otherwise fetch
      if (typeof response.data.unreadCount === 'number') {
        setUnreadCount(response.data.unreadCount);
      } else {
        setUnreadCount(0); // Assume all are read
        fetchUnreadCount(); // Verify with server
      }
      // Optionally, refresh the list if it shows mixed read/unread
      // fetchRecentNotifications(); 
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Add user feedback
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await API.delete(`/notifications/${notificationId}`);
  
      // Optimistically update local list
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
      
      // Fetch the accurate count from the server
      fetchUnreadCount();
      
      // Nếu danh sách trở nên ít thông báo và còn có thể tải thêm, tải thêm để lấp đầy
      setTimeout(() => {
        if (hasMore && !loadingMore) {
          loadMoreNotifications();
        }
      }, 100);

    } catch (error) {
      console.error(`Error deleting notification ${notificationId}:`, error);
      alert('Không thể xóa thông báo. Vui lòng thử lại.');
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await API.delete('/notifications'); // This is user-specific delete all
      setNotifications([]);
      setUnreadCount(0);
      // Reset pagination state
      setCurrentPage(1);
      setHasMore(false);
      setTotalPages(1);
      // fetchUnreadCount will confirm 0.
      fetchUnreadCount(); 
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      alert('Không thể xóa tất cả thông báo. Vui lòng thử lại.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return formatNotificationTime(timestamp);
  };

  if (!hasActiveSession) {
    return null;
  }

  // Đếm thông báo khẩn cấp / quan trọng chưa đọc
  const urgentMeta = React.useMemo(() => {
    let urgentCount = 0;
    let highCount = 0;
    notifications.forEach(n => {
      if (n.isRead) return;
      const dType = detectNotificationType(n.message, n.type);
      const dPri = detectNotificationPriority(n.message, dType, n.priority);
      if (dPri === 'urgent') urgentCount++;
      else if (dPri === 'high') highCount++;
    });
    return { urgentCount, highCount, hasUrgent: urgentCount > 0, hasHigh: highCount > 0 };
  }, [notifications]);

  console.log('[NotificationBell] Rendering - Loading:', loading, 'Notifications:', notifications, 'Unread Count:', unreadCount);

  const bellSeverityClass = urgentMeta.hasUrgent ? 'bell-urgent' : urgentMeta.hasHigh ? 'bell-high' : '';

  return (
    <div className={`notification-bell-container ${bellSeverityClass}`} ref={dropdownRef} data-urgent={urgentMeta.urgentCount} data-high={urgentMeta.highCount}>
      {console.log('[NotificationBell] Main div rendering - Dropdown focus')}
      <div className="bell-icon" onClick={handleBellClick}>
        <i className="fas fa-bell"></i>
        {unreadCount > 0 && (
          <span
            className="notification-count"
            data-count={unreadCount > 99 ? "99+" : unreadCount.toString()}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>

      {showDropdown && (
        <>
          {console.log('[NotificationBell] Dropdown rendering block entered. Notifications count:', notifications.length)}
          <div className="notification-dropdown">
            <div className="notification-header">
              <h4>Thông Báo</h4>
              <div className="notification-actions-header">
                <button 
                  className="icon-btn mark-all-read-icon"
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                  title="Đánh dấu tất cả đã đọc"
                >
                  <i className="fas fa-check-double"></i>
                </button>
                <button 
                  className="icon-btn delete-all-icon"
                  onClick={deleteAllNotifications}
                  disabled={notifications.length === 0}
                  title="Xóa tất cả"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>

            <div className="notification-filter">
              <div className={`filter-tabs-container ${!canScrollLeft ? 'hide-left-gradient' : ''} ${!canScrollRight ? 'hide-right-gradient' : ''}`}>
                <button 
                  className="scroll-button left"
                  onClick={scrollToLeft}
                  disabled={!canScrollLeft}
                  title="Cuộn trái"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                
                <div 
                  className="filter-tabs"
                  ref={filterTabsRef}
                  onScroll={checkScrollability}
                >
                  {notificationCategories.map(category => (
                    <button
                      key={category.value}
                      className={`filter-tab ${selectedFilter === category.value ? 'active' : ''}`}
                      onClick={() => handleFilterChange(category.value)}
                      title={category.label}
                    >
                      <i className={category.icon}></i>
                      <span className="filter-label">{category.label}</span>
                    </button>
                  ))}
                </div>
                
                <button 
                  className="scroll-button right"
                  onClick={scrollToRight}
                  disabled={!canScrollRight}
                  title="Cuộn phải"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <div 
              className="notification-body"
              ref={notificationListRef}
              onScroll={handleScroll}
            >
              {loading ? (
                <div className="loading-notifications">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Đang tải thông báo...</p>
                </div>
              ) : notifications && notifications.length > 0 ? (
                <>
                  {(() => {
                    const filteredNotifications = getFilteredNotifications(notifications, selectedFilter);
                    return filteredNotifications.length > 0 ? (
                      <ul className="notification-list">
                        {filteredNotifications.map((notification) => {
                      // Auto-detect type and priority if not set properly
                      const detectedType = detectNotificationType(notification.message, notification.type);
                      const detectedPriority = detectNotificationPriority(notification.message, detectedType, notification.priority);
                      const typeInfo = getNotificationTypeInfo(detectedType, detectedPriority);
                      return (
                        <li
                          key={notification._id}
                          className={`notification-item ${!notification.isRead ? 'unread' : ''} ${typeInfo.className}`}
                        >
                          <div className="notification-icon">
                            <i className={typeInfo.icon}></i>
                          </div>
                          <div
                            className="notification-content"
                            onClick={() => {
                              if (!notification.isRead) {
                                markAsRead(notification._id);
                              }
                              if (notification.link) {
                                // Sử dụng React Router navigate thay vì window.location.href
                                navigate(notification.link);
                              }
                              setShowDropdown(false);
                            }}
                          >
                            <div className="notification-labels">
                              <span className={`notification-type-label type-${detectedType?.toLowerCase() || 'system'}`}>
                                {typeInfo.label}
                              </span>
                              {detectedPriority && detectedPriority !== 'normal' && (
                                <span className={`notification-priority-label priority-${detectedPriority}`}>
                                  {detectedPriority === 'urgent' ? 'Khẩn cấp' : 
                                   detectedPriority === 'high' ? 'Cao' : 
                                   detectedPriority === 'low' ? 'Thấp' : 'Bình thường'}
                                </span>
                              )}
                            </div>
                            <p>{notification.message}</p>
                            <span className="notification-time">{formatDate(notification.createdAt)}</span>
                          </div>
                          <div className="notification-actions">
                            <button
                              className="delete-notification-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification._id);
                              }}
                            >
                              Xóa
                            </button>
                          </div>
                        </li>
                        );
                        })}
                      </ul>
                    ) : (
                      <div className="no-notifications">
                        <i className="fas fa-filter"></i>
                        <p>Không có thông báo nào trong danh mục này</p>
                      </div>
                    );
                  })()}
                  
                  {/* Loading indicator cho infinite scroll */}
                  {loadingMore && (
                    <div className="loading-more">
                      <i className="fas fa-spinner fa-spin"></i>
                      <p>Đang tải thêm...</p>
                    </div>
                  )}
                  
                  {/* Nút Load More manual (backup cho infinite scroll) */}
                  {hasMore && !loadingMore && notifications.length > 0 && (
                    <div className="load-more-button-container">
                      <button 
                        className="load-more-button"
                        onClick={loadMoreNotifications}
                      >
                        Tải thêm thông báo
                      </button>
                    </div>
                  )}
                  
                  {/* Hiển thị thông báo khi đã tải hết */}
                  {!hasMore && notifications.length > 0 && (
                    <div className="no-more-notifications">
                      <p>Đã hiển thị tất cả thông báo ({getFilteredNotifications(notifications, selectedFilter).length} thông báo)</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-notifications">
                  <i className="fas fa-check-circle"></i>
                  <p>Không có thông báo mới</p>
                </div>
              )}
            </div>

            <div className="notification-footer">
              <div className="footer-info">
                {notifications.length > 0 && (
                  <span className="notification-count-info">
                    {selectedFilter === 'ALL' ? (
                      <>Hiển thị {notifications.length} thông báo</>
                    ) : (
                      <>Hiển thị {getFilteredNotifications(notifications, selectedFilter).length} / {notifications.length} thông báo</>
                    )}
                    {totalPages > 1 && ` (Trang ${currentPage}/${totalPages})`}
                    {hasMore && ` - Còn thêm thông báo`}
                  </span>
                )}
              </div>
              <Link to="/all-notifications" className="view-all" onClick={() => setShowDropdown(false)}>
                Xem tất cả thông báo
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
    );
};

export default NotificationBell;
